import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Cookie, ShieldCheck, BarChart3, Megaphone } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useLocale } from "@/i18n/LocaleProvider";
import {
  acceptConsent,
  declineConsent,
  getConsent,
  revokeConsent,
} from "@/lib/tracking";

/**
 * Cookiebot/Usercentrics-style preference center. Reuses the same glass dialog
 * styling as the blueprint cooldown modal. The tracking engine works with a
 * single consent flag (analytics + marketing combined), so the optional
 * category toggle maps directly to that flag.
 */
const CookieSettings = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation();
  const { localize } = useLocale();
  const [analytics, setAnalytics] = useState(false);

  // Sync toggle with the persisted choice every time the modal opens.
  useEffect(() => {
    if (open) setAnalytics(getConsent() === "granted");
  }, [open]);

  // Persist the toggle state. Switching ON loads marketing pixels immediately;
  // switching OFF after a previous grant reloads the page to unload them.
  const saveSelection = () => {
    const current = getConsent();
    if (analytics) {
      acceptConsent();
      onOpenChange(false);
    } else if (current === "granted") {
      revokeConsent(); // reloads
    } else {
      declineConsent();
      onOpenChange(false);
    }
  };

  const acceptAll = () => {
    acceptConsent();
    onOpenChange(false);
  };

  const rejectAll = () => {
    if (getConsent() === "granted") {
      revokeConsent(); // reloads
    } else {
      declineConsent();
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-border bg-card/95 backdrop-blur-xl overflow-hidden">
        <div className="absolute inset-0 tech-grid opacity-[0.08] pointer-events-none" />
        <div className="absolute -right-16 -top-16 w-56 h-56 bg-accent/[0.1] blur-[90px] rounded-full pointer-events-none" />

        <DialogHeader className="relative">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-accent/30 bg-accent/10 text-accent mb-3">
            <Cookie size={20} />
          </span>
          <p className="mono-label text-left">{t("consent.settings.eyebrow")}</p>
          <DialogTitle className="text-left text-2xl font-bold tracking-tight">
            {t("consent.settings.title")}
          </DialogTitle>
        </DialogHeader>

        <div className="relative space-y-4">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {t("consent.settings.intro")}{" "}
            <Link
              to={localize("/privacy-policy")}
              className="text-accent hover:underline"
              onClick={() => onOpenChange(false)}
            >
              {t("consent.privacyLink")}
            </Link>
            .
          </p>

          {/* Necessary — always on */}
          <div className="flex items-start gap-3 rounded-xl border border-border bg-background/40 p-4">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-accent">
              <ShieldCheck size={17} />
            </span>
            <div className="flex-1">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-foreground">
                  {t("consent.settings.necessaryTitle")}
                </p>
                <span className="mono-label text-[0.6rem] text-accent">
                  {t("consent.settings.alwaysOn")}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                {t("consent.settings.necessaryBody")}
              </p>
            </div>
          </div>

          {/* Analytics & marketing — single combined toggle */}
          <label
            htmlFor="cookie-analytics"
            className="flex items-start gap-3 rounded-xl border border-border bg-background/40 p-4 cursor-pointer"
          >
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-accent">
              <BarChart3 size={17} />
            </span>
            <div className="flex-1">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-foreground inline-flex items-center gap-2">
                  {t("consent.settings.analyticsTitle")}
                  <Megaphone size={13} className="text-muted-foreground" />
                </p>
                <Switch
                  id="cookie-analytics"
                  checked={analytics}
                  onCheckedChange={setAnalytics}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                {t("consent.settings.analyticsBody")}
              </p>
            </div>
          </label>
        </div>

        <div className="relative mt-2 flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={acceptAll}
            className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 btn-clip bg-primary text-primary-foreground btn-label-contrast font-medium transition-transform duration-200 active:scale-[0.97]"
          >
            {t("consent.accept")}
          </button>
          <button
            type="button"
            onClick={saveSelection}
            className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 btn-clip border border-border bg-background/40 text-foreground font-medium transition-colors hover:border-accent/50 active:scale-[0.97]"
          >
            {t("consent.settings.save")}
          </button>
          <button
            type="button"
            onClick={rejectAll}
            className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 btn-clip border border-border bg-background/40 text-muted-foreground font-medium transition-colors hover:text-foreground active:scale-[0.97]"
          >
            {t("consent.decline")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CookieSettings;
